import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, AlertTriangle, CheckCircle2, XCircle, TrendingUp } from 'lucide-react';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { Subscription } from '../../../../types/payments';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UpgradeSubscriptionModal } from './UpgradeSubscriptionModal';

interface SubscriptionManagerProps {
    /** When provided, skip initial fetch and use this value (null = no active sub). */
    initialSubscription?: Subscription | null;
    onChanged?: () => void;
}

export function SubscriptionManager({
    initialSubscription,
    onChanged,
}: SubscriptionManagerProps = {}) {
    const hasInitial = initialSubscription !== undefined;
    const [sub, setSub] = useState<Subscription | null>(hasInitial ? initialSubscription : null);
    const [loading, setLoading] = useState(!hasInitial);
    const [error, setError] = useState<string | null>(null);
    const [canceling, setCanceling] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const loadSub = async (force = false) => {
        setLoading(true);
        try {
            const data = await recurringPaymentsApi.getSubscription({ force });
            setSub(data.has_subscription ? data.subscription : null);
        } catch (err) {
            console.error(err);
            setError("No pudimos cargar tu suscripción actual.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasInitial) {
            setSub(initialSubscription ?? null);
            setLoading(false);
            return;
        }
        loadSub();
    }, [hasInitial, initialSubscription]);

    const handleCancel = async () => {
        setCanceling(true);
        try {
            await recurringPaymentsApi.cancelSubscription();
            toast.success("Suscripción cancelada", {
                description: "Seguirás teniendo acceso hasta el final de tu ciclo de facturación."
            });
            if (onChanged) {
                onChanged();
            } else {
                await loadSub();
            }
        } catch (err: any) {
            toast.error("Error al cancelar", {
                description: err.response?.data?.detail || "No pudimos procesar la cancelación."
            });
        } finally {
            setCanceling(false);
            setShowCancelDialog(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-4">
                <Loader2 className="w-6 h-6 animate-spin text-[#1CB3B3]" />
            </div>
        );
    }

    if (error) {
        return <p className="text-red-400 text-sm tracking-wide bg-red-500/10 p-4 rounded-xl">{error}</p>;
    }

    if (!sub) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm">
                <ShieldCheck className="w-10 h-10 text-slate-400 mx-auto mb-2" />

                <h4 className="text-slate-900 font-bold mb-1">
                    No tienes planes activos
                </h4>

                <p className="text-sm text-slate-500">
                    Suscríbete a un plan prepago recurrentemente para automatizar tus pagos mensuales.
                </p>
            </div>
        );
    }

    const statusColors: Record<string, string> = {
        active: 'text-emerald-700 bg-emerald-50 border-emerald-200',
        past_due: 'text-red-600 bg-red-50 border-red-200',
        cancelled: 'text-amber-700 bg-amber-50 border-amber-200',
        paused: 'text-slate-600 bg-slate-100 border-slate-200',
    };

    const statusIcons: Record<string, React.ReactNode> = {
        active: <CheckCircle2 className="w-4 h-4" />,
        past_due: <AlertTriangle className="w-4 h-4" />,
        cancelled: <XCircle className="w-4 h-4" />,
        paused: <AlertTriangle className="w-4 h-4" />,
    };

    const statusText: Record<string, string> = {
        active: 'Activa',
        past_due: 'Pago falló - Intentando cobrar...',
        cancelled: 'Cancelada',
        paused: 'Pausada',
    };

    const planLabels: Record<string, string> = {
        'basic_200': 'Básico (200 msjs)',
        'standard_1000': 'Estándar (1.000 msjs)',
        'premium_3000': 'Avanzado (3.000 msjs)',
    };

    const formatCOP = (val: number) =>
        val.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

    const nextBilling = new Date(sub.next_billing_date);

    // Solo mostrar el botón de upgrade si NO está en el plan máximo
    const canUpgrade = (sub.status === 'active' || sub.status === 'past_due') && sub.plan_type !== 'premium_3000';

    return (
        <>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm relative overflow-hidden">

                {/* Banner de período de gracia */}
                {sub.status === 'past_due' && (
                    <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl relative z-10 animate-in fade-in slide-in-from-top-2">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />

                            <div>
                                <p className="text-amber-700 font-bold text-sm">
                                    ⚠️ Tu pago no pudo ser procesado
                                </p>

                                <p className="text-amber-600 text-xs mt-1">
                                    Estamos reintentando el cobro automáticamente. Tienes un período de gracia de 3 días
                                    para actualizar tu tarjeta antes de que tu cuenta sea suspendida.
                                </p>

                                <p className="text-amber-600 text-xs mt-1">
                                    Intento {sub.retry_count} de 3
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Banner de cuenta suspendida */}
                {sub.status === 'paused' && (
                    <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl relative z-10 animate-in fade-in slide-in-from-top-2">
                        <div className="flex items-start gap-3">
                            <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />

                            <div>
                                <p className="text-red-700 font-bold text-sm">
                                    🚫 Tu suscripción ha sido suspendida
                                </p>

                                <p className="text-red-600 text-xs mt-1">
                                    No pudimos procesar tu pago después de varios intentos.
                                    Para reactivar tu cuenta, actualiza tu método de pago y crea una nueva suscripción.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-10">

                    <div>
                        <div className="flex flex-wrap items-center gap-3 mb-2">

                            <h3 className="text-xl font-bold text-slate-900">
                                Tu Plan Actual
                            </h3>

                            <span
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusColors[sub.status]}`}
                            >
                                {statusIcons[sub.status]}
                                {statusText[sub.status]}
                            </span>
                        </div>

                        <p className="text-3xl font-black text-slate-900">
                            {planLabels[sub.plan_type] || sub.plan_type}
                        </p>

                        <p className="text-[#159A9C] font-bold text-lg mt-1">
                            {formatCOP(sub.amount)}

                            <span className="text-slate-500 font-medium text-sm">
                                {' '}/ mes
                            </span>
                        </p>
                    </div>

                    {sub.status === 'active' ? (
                        <div className="bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl text-right min-w-[200px]">

                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest mb-1">
                                PRÓXIMO COBRO
                            </p>

                            <p className="text-slate-900 font-bold">
                                {nextBilling.toLocaleDateString('es-CO', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </p>
                        </div>
                    ) : sub.status === 'cancelled' ? (
                        <div className="bg-amber-50 border border-amber-200 px-5 py-4 rounded-xl text-right min-w-[200px]">

                            <p className="text-[10px] text-amber-600 uppercase font-bold tracking-widest mb-1">
                                VIGENCIA HASTA
                            </p>

                            <p className="text-amber-700 font-bold">
                                {nextBilling.toLocaleDateString('es-CO')}
                            </p>
                        </div>
                    ) : null}
                </div>

                {(sub.status === 'active' || sub.status === 'past_due') && (
                    <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-end gap-3 flex-wrap">

                        {/* Botón Mejorar suscripción */}
                        {canUpgrade && (
                            <button
                                onClick={() => setShowUpgradeModal(true)}
                                className="flex items-center gap-2 text-sm font-semibold text-[#159A9C] border border-[#CDEEEE] hover:bg-[#E8F7F7] px-4 py-2 rounded-lg transition-all"
                            >
                                <TrendingUp className="w-4 h-4" />
                                Mejorar suscripción
                            </button>
                        )}

                        {/* Botón Cancelar */}
                        <button
                            onClick={() => setShowCancelDialog(true)}
                            disabled={canceling}
                            className="text-sm font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg transition-all"
                        >
                            {canceling
                                ? 'Procesando...'
                                : 'Cancelar suscripción'
                            }
                        </button>

                    </div>
                )}

            </div>

            {/* Modal de cancelación */}
            <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                <AlertDialogContent className="bg-white border-slate-200 text-slate-900 shadow-xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-slate-900">
                            ¿Seguro que deseas cancelar?
                        </AlertDialogTitle>

                        <AlertDialogDescription className="text-slate-500">
                            Podrás seguir usando tus mensajes hasta el final del ciclo de facturación (
                            {nextBilling.toLocaleDateString('es-CO')}), pero no se realizará ningún cobro adicional el próximo mes.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 hover:text-slate-900">
                            Mantener plan
                        </AlertDialogCancel>

                        <AlertDialogAction
                            onClick={handleCancel}
                            className="bg-red-500 hover:bg-red-600 text-white"
                        >
                            Sí, cancelar suscripción
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Modal de upgrade */}
            {sub && (
                <UpgradeSubscriptionModal
                    open={showUpgradeModal}
                    onOpenChange={setShowUpgradeModal}
                    currentPlan={sub.plan_type}
                    onUpgradeSuccess={async () => {
                        if (onChanged) {
                            onChanged();
                        } else {
                            await loadSub();
                        }
                    }}
                />
            )}
        </>
    );
}
